/**
 * ParentAvatarPanel — Alfred UI for setting a parent's profile picture.
 *
 * Embedded inside the Parent profile modal next to the GuardiansPanel. Shows the
 * parent's current avatar and lets an admin upload a new one (Cloudinary →
 * User.image). Self-contained — patches the parent directly via the API client.
 */
import { Avatar, CircularProgress } from '@material-ui/core'
import PropTypes from 'prop-types'
import React from 'react'

import { updateParentImage } from 'api'
import CloudFileUploader from 'app/components/CloudFileUploader'
import {
  Card,
  CardBody,
  CardHeader,
  CardTitle
} from 'app/components/generic/Card'
import { uploadPresets } from 'app/config'
import constants from 'app/constants'
import { eventAlert, showToast } from 'app/helpers'

const ParentAvatarPanel = ({ parentUuid, initialImage, parentName }) => {
  const [image, setImage] = React.useState(initialImage || '')
  const [saving, setSaving] = React.useState(false)

  const handleUpload = async (url) => {
    if (!url || !parentUuid) return
    setSaving(true)
    try {
      await updateParentImage(parentUuid, url)
      setImage(url)
      showToast('Profile picture updated', constants.SUCCESS)
    } catch (error) {
      eventAlert(
        error?.response?.data?.message ||
          error?.message ||
          'Failed to update picture',
        constants.ERROR
      )
    } finally {
      setSaving(false)
    }
  }

  return (
    <Card>
      <CardHeader tone="blue">
        <CardTitle>Profile picture</CardTitle>
      </CardHeader>
      <CardBody>
        <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
          <Avatar src={image || undefined} style={{ width: 64, height: 64 }}>
            {(parentName || '?').charAt(0).toUpperCase()}
          </Avatar>
          {saving ? (
            <CircularProgress size={20} />
          ) : (
            <CloudFileUploader
              name="image"
              label="photo"
              uploadedFileUrl={image || ''}
              uploadPreset={uploadPresets[constants.USER]}
              fileUrlOnly
              onUpload={({ data }) => handleUpload(data?.secure_url || '')}
              setFieldValue={() => null}
            />
          )}
        </div>
      </CardBody>
    </Card>
  )
}

ParentAvatarPanel.propTypes = {
  parentUuid: PropTypes.string,
  initialImage: PropTypes.string,
  parentName: PropTypes.string
}

export { ParentAvatarPanel }
export default ParentAvatarPanel
